import { Component, OnDestroy, OnInit } from '@angular/core';

import { RouterOutlet } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NotificationService } from './core/services/notification.service';
import { PwaInstallService } from './core/services/pwa-install.service';
import { Subject, fromEvent } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from '../environments/environment';

@Component({
    selector: 'ph-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
    isInstallable = false;
    showInstallPrompt = false;
    private destroy$ = new Subject<void>();

    constructor(
        private notificationService: NotificationService,
        private pwaInstall: PwaInstallService,
        private swUpdate: SwUpdate,
        private snackBar: MatSnackBar,
    ) {}

    ngOnInit(): void {
        if (environment.releaseStage === 'production') {
            this.initializePwaInstall();
        }

        this.initializePwaUpdates();

        // Initialize notifications after a short delay
        // This gives the user time to interact with the page first
        // (some browsers require user interaction before showing permission prompts)
        setTimeout(() => {
            this.initializeNotifications();
        }, 2000);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initializePwaUpdates(): void {
        if (!this.swUpdate.isEnabled) return;

        // Cuando hay una versión nueva lista, mostrar aviso al usuario
        this.swUpdate.versionUpdates.pipe(
            filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
            takeUntil(this.destroy$),
        ).subscribe(() => this._showUpdateSnackBar());

        // Comprobar al abrir la app
        this.swUpdate.checkForUpdate();

        // Comprobar cuando la app vuelve del segundo plano
        fromEvent(document, 'visibilitychange').pipe(
            takeUntil(this.destroy$),
        ).subscribe(() => {
            if (document.visibilityState === 'visible') {
                this.swUpdate.checkForUpdate();
            }
        });
    }

    private _showUpdateSnackBar(): void {
        const ref = this.snackBar.open(
            'Nueva versión disponible',
            'Actualizar',
            { horizontalPosition: 'center', verticalPosition: 'bottom', duration: 0 },
        );
        ref.onAction().subscribe(() => {
            this.swUpdate.activateUpdate().then(() => document.location.reload());
        });
    }

    /**
     * Initialize PWA installation
     */
    private initializePwaInstall(): void {
        this.pwaInstall
            .getInstallableState()
            .pipe(takeUntil(this.destroy$))
            .subscribe((isInstallable) => {
                this.isInstallable = isInstallable;
                if (isInstallable) {
                    // Show install prompt after a delay to avoid being too intrusive
                    setTimeout(() => {
                        this.showInstallPrompt = true;
                        console.log('✅ PWA is installable - showing prompt');
                    }, 3000);
                }
            });
    }

    /**
     * Handle install button click
     */
    async installApp(): Promise<void> {
        await this.pwaInstall.install();
        this.showInstallPrompt = false;
    }

    /**
     * Handle dismiss button click
     */
    dismissInstallPrompt(): void {
        this.showInstallPrompt = false;
    }

    /**
     * Initialize Firebase Cloud Messaging notifications
     */
    private async initializeNotifications(): Promise<void> {
        // Check if notifications are supported
        if (!this.notificationService.isNotificationSupported()) {
            console.warn('⚠️ Push notifications are not supported in this browser');
            return;
        }

        // Check current permission status
        const currentPermission = this.notificationService.getPermissionStatus();
        console.log('📱 Current notification permission:', currentPermission);

        // If permission already granted, just get the token
        if (currentPermission === 'granted') {
            const token = await this.notificationService.requestPermission();
            if (token) {
                console.log('✅ FCM Token obtained:', token);
                console.log('📋 Copy this token to test notifications from Firebase Console');
            }
            // Start listening to foreground messages
            this.notificationService.listenToMessages();
            return;
        }

        // If permission was previously denied, don't ask again
        if (currentPermission === 'denied') {
            console.warn('❌ Notification permission was previously denied');
            console.log('💡 To enable notifications, please allow them in your browser settings');
            return;
        }

        // If permission is 'default', request it
        if (currentPermission === 'default') {
            console.log('🔔 Requesting notification permission...');

            const token = await this.notificationService.requestPermission();

            if (token) {
                console.log('✅ Notification permission granted!');
                console.log('🔑 FCM Registration Token:', token);
                console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
                console.log('📋 COPY THIS TOKEN TO TEST NOTIFICATIONS:');
                console.log(token);
                console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
                console.log('');
                console.log('🧪 To test, go to Firebase Console:');
                console.log('1. Cloud Messaging > Send test message');
                console.log('2. Paste the token above');
                console.log('3. Send the notification');
                console.log('');

                // Start listening to foreground messages
                this.notificationService.listenToMessages();
            } else {
                console.warn('⚠️ Failed to get FCM token');
                const error = this.notificationService.error();
                if (error) {
                    console.error('Error:', error);
                }
            }
        }
    }
}
